import moment from 'moment';
import { Box, TableCell, TableRow, Tooltip } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';

import { VendorPaymentHistoryTableItem } from './types';
import { StatusChip } from '../../components/StatusChip';
import { DeductionStatusMap } from '../PaymentsView/SettlementsView/constants';
import { useNavigate } from 'react-router';
import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { palette } from '../../palette';
import { getAccountingSyncStatusColor } from '../../helpers/getAccountingSyncStatusColor';
import { AccountingSyncStatus } from '../../types';

interface PaymentHistoryTableDataRowProps {
  data: VendorPaymentHistoryTableItem[];
  isAccountingIntegrated?: boolean;
}

interface NetSuiteLinks {
  billLink: string | null;
  billPaymentLink: string | null;
  billError?: string | null;
  billPaymentError?: string | null;
  billSyncStatus?: AccountingSyncStatus | null;
  billPaymentSyncStatus?: AccountingSyncStatus | null;
}

interface CheckDetails {
  payee: string | null;
  nickname: string | null;
  payeeName: string | null;
  recipientName: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  countryState: string | null;
  zip: string | null;
  phoneNumber: string | null;
}

interface AchDetails {
  payee: string | null;
  nickname: string | null;
  accountHolderName: string | null;
  accountType: string | null;
  routingNumber: string | null;
  accountNumber: string | null;
}

interface FormattedVendorPaymentHistoryTableItem {
  caseId: number;
  updatedAt: string;
  caseReferenceId: string;
  plaintiffName: string;
  method: string;
  nickname: string;
  amount: string;
  status: string;
  netSuite: NetSuiteLinks;
  checkDetails: CheckDetails | null | undefined;
  achDetails: AchDetails | null | undefined;
  recoveredAmount: number | undefined;
}

const PaymentHistoryTableDataRow = ({ data, isAccountingIntegrated }: PaymentHistoryTableDataRowProps) => {
  const navigate = useNavigate();
  const onTableRowClick = (id: number) => {
    navigate(`/cases/${String(id)}/home`);
  };

  const formattedTableData: FormattedVendorPaymentHistoryTableItem[] = data.map((payment) => ({
    caseId: payment.caseId,
    updatedAt: moment(payment.updatedAt).format('MM/DD/YYYY'),
    caseReferenceId: payment.caseReferenceId,
    plaintiffName: payment.plaintiffName,
    method: payment.method,
    nickname:
      (payment.method === 'CHECK'
        ? payment.checkDetails?.nickname
        : payment.achDetails?.nickname) ?? '-',
    amount: numberToUSDCurrency(payment.amount),
    status: payment.status,
    netSuite: {
      billLink: payment.billLink,
      billPaymentLink: payment.billPaymentLink,
      billError: payment.billError,
      billPaymentError: payment.billPaymentError,
      billSyncStatus: payment.billSyncStatus,
      billPaymentSyncStatus: payment.billPaymentSyncStatus,
    },
    checkDetails: payment.checkDetails,
    achDetails: payment.achDetails,
    recoveredAmount: payment.recoveredAmount,
  }));

  return (
    <>
      {formattedTableData.map((tableItem) => (
        <TableRow
          key={`${tableItem.caseId}-${tableItem.updatedAt}-${tableItem.amount}`}
          sx={{ width: '100%', '&:hover': { cursor: 'default' } }}
          onClick={() => onTableRowClick(tableItem.caseId)}
        >
          {Object.entries(tableItem).map(([key, value], idx) => {
            if (key === 'caseId') return null;
            if (key === 'netSuite' && !isAccountingIntegrated) return null;
            if (key === 'checkDetails' || key === 'achDetails' || key === 'recoveredAmount') return null;

            return (
              <TableCell key={idx} sx={{ whiteSpace: 'normal !important' }}>
                {key === 'status' && (tableItem.recoveredAmount ?? 0) > 0 ? (
                  <StatusChip
                    label={`Recovered ${numberToUSDCurrency(tableItem.recoveredAmount as number)}`}
                    color={palette.warning.main}
                  />
                ) : key === 'status' ? (
                  <StatusChip
                    label={
                      DeductionStatusMap[value as keyof typeof DeductionStatusMap]?.label ||
                      String(value)
                    }
                    color={
                      DeductionStatusMap[value as keyof typeof DeductionStatusMap]?.color ||
                      'default'
                    }
                  />
                ) : key === 'method' ? (
                  (() => {
                    const methodStr = String(value || '');
                    const checkDetails = tableItem.checkDetails;
                    const achDetails = tableItem.achDetails;

                    if (methodStr === 'CHECK' && checkDetails) {
                      const d = checkDetails;
                      const addressParts = [d.address1, d.address2].filter(Boolean).join(', ');
                      const cityStateZip = [d.city, d.countryState, d.zip].filter(Boolean).join(', ');
                      const fullAddress = [addressParts, cityStateZip].filter(Boolean).join(', ');
                      const displayPayee = d.payeeName || d.payee;
                      return (
                        <Tooltip
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: (theme) => ({
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.light,
                                borderRadius: '5px',
                                boxShadow: '3px',
                                fontFamily: 'Inter',
                                fontSize: '13px',
                                fontWeight: 500,
                                padding: '5px',
                                maxWidth: '250px',
                              }),
                            },
                          }}
                          title={
                            <Box>
                              {displayPayee && <div>Payee: {displayPayee}</div>}
                              {fullAddress && <div>Address: {fullAddress}</div>}
                              {d.phoneNumber && <div>Phone Number: {d.phoneNumber}</div>}
                            </Box>
                          }
                        >
                          <span>{methodStr || '-'}</span>
                        </Tooltip>
                      );
                    }
                    if (methodStr === 'ACH' && achDetails) {
                      const d = achDetails;
                      return (
                        <Tooltip
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: (theme) => ({
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.light,
                                borderRadius: '5px',
                                boxShadow: '3px',
                                fontFamily: 'Inter',
                                fontSize: '13px',
                                fontWeight: 500,
                                padding: '5px',
                                maxWidth: '250px',
                              }),
                            },
                          }}
                          title={
                            <Box>
                              {d.accountHolderName && <div>Payee: {d.accountHolderName}</div>}
                              {d.accountNumber && <div>Account Number: {d.accountNumber}</div>}
                              {d.accountType && <div>Account Type: {d.accountType}</div>}
                              {d.routingNumber && <div>Routing Number: {d.routingNumber}</div>}
                            </Box>
                          }
                        >
                          <span>{methodStr || '-'}</span>
                        </Tooltip>
                      );
                    }
                    return <span>{methodStr || '-'}</span>;
                  })()
                ) : key === 'netSuite' ? (
                  (() => {
                    const links = value as NetSuiteLinks;

                    return links.billLink ||
                      links.billPaymentLink ||
                      links.billError ||
                      links.billPaymentError ||
                      links.billSyncStatus ||
                      links.billPaymentSyncStatus ? (
                      <Box display="flex" gap="10px" alignItems="center">
                        {(links.billLink || links.billError || links.billSyncStatus) &&
                          (links.billLink || links.billError ? (
                            <Tooltip
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: (theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    color: theme.palette.primary.light,
                                    borderRadius: '5px',
                                    boxShadow: '3px',
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    padding: '5px',
                                    maxWidth: '180px',
                                  }),
                                },
                              }}
                              title={links.billError || 'View Bill'}
                            >
                              <Box
                                sx={{
                                  paddingTop: '5px',
                                  cursor: links.billLink ? 'pointer' : 'default',
                                }}
                                onClick={
                                  links.billLink
                                    ? (e) => { e.stopPropagation(); window.open(links.billLink || '', '_blank'); }
                                    : undefined
                                }
                              >
                                <ReceiptIcon
                                  fontSize="small"
                                  sx={{ color: getAccountingSyncStatusColor(links.billSyncStatus) }}
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ paddingTop: '5px' }}>
                              <ReceiptIcon
                                fontSize="small"
                                sx={{ color: getAccountingSyncStatusColor(links.billSyncStatus) }}
                              />
                            </Box>
                          ))}

                        {(links.billPaymentLink ||
                          links.billPaymentError ||
                          links.billPaymentSyncStatus) &&
                          (links.billPaymentLink || links.billPaymentError ? (
                            <Tooltip
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: (theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    color: theme.palette.primary.light,
                                    borderRadius: '5px',
                                    boxShadow: '3px',
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    padding: '5px',
                                    maxWidth: '180px',
                                  }),
                                },
                              }}
                              title={links.billPaymentError || 'View Bill Payment'}
                            >
                              <Box
                                sx={{
                                  paddingTop: '5px',
                                  cursor: links.billPaymentLink ? 'pointer' : 'default',
                                }}
                                onClick={
                                  links.billPaymentLink
                                    ? (e) => { e.stopPropagation(); window.open(links.billPaymentLink || '', '_blank'); }
                                    : undefined
                                }
                              >
                                <PaymentsIcon
                                  fontSize="small"
                                  sx={{
                                    color: getAccountingSyncStatusColor(
                                      links.billPaymentSyncStatus,
                                    ),
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ paddingTop: '5px' }}>
                              <PaymentsIcon
                                fontSize="small"
                                sx={{
                                  color: getAccountingSyncStatusColor(links.billPaymentSyncStatus),
                                }}
                              />
                            </Box>
                          ))}
                      </Box>
                    ) : null;
                  })()
                ) : value === null || value === undefined || value === '' ? (
                  '-'
                ) : (
                  String(value)
                )}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </>
  );
};

export default PaymentHistoryTableDataRow;
